import type { PropertiesTableProps } from '../../shared/types'

export const DialogProperties: PropertiesTableProps = {
  variant: {
    doc: 'The dialog variant. Can either be `information` or `confirmation`. Defaults to `information`.',
    type: ['"information"', '"confirmation"'],
    status: 'optional',
  },
  title: {
    doc: 'The dialog title. Displays on the very top of the content.',
    type: 'React.ReactNode',
    status: 'optional',
  },
  minWidth: {
    doc: "The minimum Dialog content width, defined by a CSS width value like `50vw` (50% of the viewport). Be careful when using fixed `minWidth` so you don't break responsiveness. Defaults to `30rem` (average width is set to `60vw`).",
    type: ['string', 'number'],
    status: 'optional',
  },
  maxWidth: {
    doc: 'The maximum Dialog content width, defined by a CSS width value like `20rem`. Defaults to `60rem` (average width is set to `60vw`).',
    type: ['string', 'number'],
    status: 'optional',
  },
  className: {
    doc: 'Give the Dialog content a class name (maps to `dnb-dialog`).',
    type: 'string',
    status: 'optional',
  },
  spacing: {
    doc: 'If set to `false` then the dialog content will be shown without any spacing. Defaults to `true`.',
    type: 'boolean',
    status: 'optional',
  },
  preventCoreStyle: {
    doc: 'By default the dialog content gets added the core style class `dnb-core-style`. Use `false` to disable this behavior.',
    type: 'boolean',
    status: 'optional',
  },
  navContent: {
    doc: 'The content which will appear in the navigation, above the header, and side-by-side the close button.',
    type: 'React.ReactNode',
    status: 'optional',
  },
  headerContent: {
    doc: 'The content which will appear in the header of the dialog.',
    type: 'React.ReactNode',
    status: 'optional',
  },
  modalContent: {
    doc: 'The content which will appear when triggering the dialog.',
    type: 'React.ReactNode',
    status: 'optional',
  },
  description: {
    doc: 'A description will be positioned below the title, but before the content. Used for Dialog variant `confirmation` to further describe what the actions will do.',
    type: 'React.ReactNode',
    status: 'optional',
  },
  status: {
    doc: 'Provide a status message that will be displayed below the action buttons.',
    type: ['string', 'React.ReactNode'],
    status: 'optional',
  },
  verticalAlignment: {
    doc: 'Define the vertical alignment of the container. Can be set to `top` or `center`. Defaults to `center`.',
    type: ['"top"', '"center"'],
    status: 'optional',
  },
  alignContent: {
    doc: 'Define the inner horizontal alignment of the content. Can be set to `left`, `center`, `right` and `centered`. If `centered`, then the content will also be centered vertically. Defaults to `left`.',
    type: ['"left"', '"center"', '"centered"', '"right"'],
    status: 'optional',
  },
  fullscreen: {
    doc: 'If set to `true` then the dialog content will be shown as fullscreen, without showing the original content behind. Can be set to `false` to omit the auto fullscreen. Defaults to `auto`.',
    type: ['boolean', '"auto"'],
    status: 'optional',
  },
  icon: {
    doc: 'An icon to display at the top of the component. Should be of size medium, so make sure you import the `_medium` version of the Eufemia icon.',
    type: 'React.ReactNode',
    status: 'optional',
  },
  confirmType: {
    doc: 'For variant confirmation, the dialog is either an informational (`information`) or a warning (`warning`) message. Defaults to `information`.',
    type: ['"information"', '"warning"'],
    status: 'optional',
  },
  declineText: {
    doc: 'For dialog actions, give a custom text for the decline button.',
    type: 'React.ReactNode',
    status: 'optional',
  },
  confirmText: {
    doc: 'For dialog actions, give a custom text for the confirmation button.',
    type: 'React.ReactNode',
    status: 'optional',
  },
  hideDecline: {
    doc: 'For variant confirmation, hide the default decline button and only show the confirmation button.',
    type: 'boolean',
    status: 'optional',
  },
  hideConfirm: {
    doc: 'For variant confirmation, hide the default confirm button and only show the decline button.',
    type: 'boolean',
    status: 'optional',
  },
  scrollRef: {
    doc: 'To get the scroll Element, pass in your own React ref.',
    type: 'React.RefObject',
    status: 'optional',
  },
  scrollbarGutter: {
    doc: 'Reserves space for the scrollbar gutter, preventing layout shifts when content overflows. By default, it enables `stable` for the `information` variant with spacing. Set to `false` to disable.',
    type: ['"stable"', 'false'],
    status: 'optional',
  },
  contentRef: {
    doc: 'To get the inner content Element, pass in your own React ref.',
    type: 'React.RefObject',
    status: 'optional',
  },
  '[Space](/uilib/layout/space/properties)': {
    doc: 'Spacing properties like `top` or `bottom` are supported.',
    type: ['string', 'object'],
    status: 'optional',
  },
}

export const DialogEvents: PropertiesTableProps = {
  onConfirm: {
    doc: 'For variant confirmation, handle the confirm action click. Provided with the mouse event and the Modal function `close` as arguments.',
    type: 'function',
    status: 'optional',
  },
  onDecline: {
    doc: 'For variant confirmation, handle the decline action click. Provided with the mouse event and the Modal function `close` as arguments.',
    type: 'function',
    status: 'optional',
  },
}
